import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import {
  createThirdPartyTemplate,
  removeEmailTemplate,
  setDefaultEmailTemplate,
  thirdPartyTemplatesListQuery,
  thirdPartyTemplatesShellQuery,
  updateThirdPartyTemplate,
  type EmailTemplateRow,
  type ProvidersSaved,
} from '../../api/settingsEaseedeskEmailTemplates'
import { renderSubject, renderTemplate, SAMPLE_VALUES } from '../../easeedesk/thirdPartyEmail'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { TemplateModal, TextPreview, updatedLabel } from './EmailTemplatesPage'

/**
 * Settings → Easeedesk → Ticket notifications → Templates → Third party
 * escalation templates.
 *
 * Sabloanele EMAILULUI CATRE FURNIZOR din formularul "Waiting for Third Party"
 * al tichetului. Stau in aceeasi tabela cu sabloanele de email ale
 * notificarilor (`template_kind = third_party`), se scriu in aceeasi fereastra
 * (`TemplateModal`), cu acelasi drept - dar au variabilele LOR: numai ce se
 * poate spune unei firme din afara (aplicatia, versiunea, firma clientului,
 * problema, serverele, contactul), fara linkuri spre portal si fara ID-uri.
 *
 * Sunt TEXT, nu HTML: previzualizarea le umple cu date de proba si le arata
 * ca text, exact cum le arata si formularul tichetului.
 *
 * Unul e IMPLICIT (vine ales in formular). Cat timp nu e niciunul, formularul
 * foloseste sablonul scris in cod - aratat aici, cu "Customize" ca sa devina
 * al furnizorului de servicii.
 *
 * SABLONUL PE FURNIZOR (7 octombrie 2026): "Used for providers" leaga sablonul
 * de furnizori externi; formularul il alege singur pentru ei. Un furnizor are
 * cel mult UN sablon: ales aici cand era al altuia, se MUTA - fereastra spune
 * dinainte al cui e ("now: <sablon>"), iar dupa salvare un mesaj spune ce s-a
 * mutat. Lista arata coloana "Providers".
 */

function previewOf(subject: string, body: string) {
  return { subject: renderSubject(subject, SAMPLE_VALUES), body: renderTemplate(body, SAMPLE_VALUES) }
}

/** Mutarile (un furnizor = un sablon) se spun, nu se fac pe tacute. */
function saved(answer: ProvidersSaved): ProvidersSaved {
  if (answer.moved && answer.moved.length > 0 && answer.message) toast(answer.message, 'Provider moved', 'info')
  return answer
}

/**
 * "Used for providers": alegere multipla din furnizorii externi, cu cautare.
 * Langa un furnizor legat de ALT sablon scrie al cui e - ales aici, se muta.
 */
export function ProviderPicker({
  providers,
  chosen,
  onChange,
  owners,
}: {
  providers: Array<{ id: number; name: string }>
  chosen: number[]
  onChange(next: number[]): void
  /** furnizor -> numele sablonului de care e legat acum (altul decat cel deschis) */
  owners: Record<number, string>
}) {
  const [search, setSearch] = useState('')
  const needle = search.trim().toLowerCase()
  const shown = providers.filter((p) => needle === '' || p.name.toLowerCase().includes(needle))
  const toggle = (id: number) => onChange(chosen.includes(id) ? chosen.filter((c) => c !== id) : [...chosen, id])

  return (
    <div className="form-group tp-providers">
      <label className="control-label">Used for providers</label>
      <p className="help-block" style={{ marginTop: 0 }}>
        Waiting for Third Party picks this template by itself for these providers. A provider has one template: choosing one that uses
        another template moves it here. No provider = a general template.
      </p>
      {providers.length === 0 ? (
        <p className="tp-providers-empty">
          No external providers yet - add them in{' '}
          <a href="/app/settings/easeedesk/external_service_providers">Settings &gt; Easeedesk &gt; External service providers</a>.
        </p>
      ) : (
        <>
          <input
            type="search"
            className="form-control input-sm tp-providers-search"
            placeholder="Search providers..."
            aria-label="Search providers"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 3, padding: '4px 8px', marginTop: 4 }}>
            {shown.length === 0 ? (
              <p className="help-block" style={{ margin: '4px 0' }}>
                No provider matches.
              </p>
            ) : (
              shown.map((p) => (
                <div className="checkbox" key={p.id} style={{ margin: '2px 0' }}>
                  <label>
                    <input type="checkbox" className="tp-provider" value={p.id} checked={chosen.includes(p.id)} onChange={() => toggle(p.id)} />{' '}
                    {p.name}
                    {owners[p.id] ? (
                      <span className="text-muted tp-provider-owner">
                        {' '}
                        (now: {owners[p.id]}
                        {chosen.includes(p.id) ? ' - moves here' : ''})
                      </span>
                    ) : null}
                  </label>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}

export function ThirdPartyTemplatesPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(thirdPartyTemplatesShellQuery())
  const list = useQuery(thirdPartyTemplatesListQuery())
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<EmailTemplateRow | null>(null)
  const [preview, setPreview] = useState<{ subject: string; body: string } | null>(null)
  /** Furnizorii alesi in fereastra deschisa (creare sau editare). */
  const [chosen, setChosen] = useState<number[]>([])

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'email_templates', 'third_party'] })
  }
  const failed = (error: unknown) =>
    toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')

  const remove = useMutation({
    mutationFn: (template: number) => removeEmailTemplate(template),
    onSuccess: () => {
      toast('Template removed.', 'Success')
      reload()
    },
    onError: failed,
  })
  const makeDefault = useMutation({
    mutationFn: (template: number) => setDefaultEmailTemplate(template),
    onSuccess: () => {
      toast('Default template saved.', 'Success')
      reload()
    },
    onError: failed,
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed what="The third party escalation templates" error={shell.error} onRetry={() => void shell.refetch()} />
  if (!shell.data) return <Spinner />

  const builtin = shell.data.builtin
  const templates = list.data?.templates ?? []
  const linking = shell.data.providersReady === true
  const providers = shell.data.providers ?? []

  /** furnizor -> sablonul lui, afara de cel deschis acum. */
  const ownersFor = (open: EmailTemplateRow | null): Record<number, string> => {
    const out: Record<number, string> = {}
    for (const t of templates) {
      if (open !== null && t.template_id === open.template_id) continue
      for (const p of t.providers ?? []) out[p.id] = t.template_name
    }
    return out
  }
  const save = (template: EmailTemplateRow | null, name: string, subject: string, body: string) => {
    const linked = linking ? chosen : undefined
    const request =
      template === null
        ? createThirdPartyTemplate(name, subject, body, linked)
        : updateThirdPartyTemplate(template.template_id, name, subject, body, linked)
    return request.then(saved)
  }
  const picker = (open: EmailTemplateRow | null) =>
    linking ? <ProviderPicker providers={providers} chosen={chosen} onChange={setChosen} owners={ownersFor(open)} /> : null
  const openNew = () => {
    setChosen([])
    setCreating(true)
  }
  const hasGeneralDefault = templates.some((t) => t.is_default)

  return (
    <div>
      <h4>
        <strong>Third party escalation</strong> templates
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        The email sent to an external provider from <strong>Waiting for Third Party</strong> on a ticket. Plain text, filled with the variables
        below; no portal links or internal IDs are ever added.
      </label>
      <br />
      {shell.data.ready ? (
        <button type="button" style={{ marginTop: 10 }} className="btn btn-success pull-right" onClick={openNew}>
          <i className="fas fa-plus" aria-hidden="true"></i> Create new template
        </button>
      ) : (
        <div className="alert alert-warning" style={{ marginTop: 10 }}>
          <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> Third party escalation templates are not set up on this server yet
          (database migration pending). The ticket form uses the built-in template below.
        </div>
      )}
      <p style={{ marginTop: 10 }}>
        Variables:{' '}
        {shell.data.options.map((o) => (
          <code key={o.token} title={o.label} style={{ display: 'inline-block', margin: '0 4px 2px 0' }}>
            {o.token}
          </code>
        ))}
      </p>
      <hr />

      {linking && templates.length > 0 && !hasGeneralDefault && (
        <div className="alert alert-info tp-no-general-default">
          <i className="fas fa-info-circle" aria-hidden="true"></i> No general template is set as default: providers without their own template
          get the built-in one.
        </div>
      )}

      <div className="list-group" id="third_party_templates">
        {list.error ? (
          <LoadFailed what="The templates" error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner />
        ) : templates.length === 0 ? (
          <section className="settings tp-builtin">
            <div className="row">
              <div className="col-md-3">
                <h5>
                  <i className="far fa-envelope-open" aria-hidden="true"></i> {builtin.name}
                </h5>
                <p className="color-green3" style={{ marginTop: 5 }}>
                  <i className="fas fa-star" aria-hidden="true"></i> Default (built-in)
                </p>
              </div>
              <div className="col-md-5" style={{ wordWrap: 'break-word' }}>
                <p>{builtin.subject}</p>
                <a
                  href="#"
                  className="color-blue"
                  onClick={(e) => {
                    e.preventDefault()
                    setPreview(previewOf(builtin.subject, builtin.body))
                  }}
                >
                  <i className="fas fa-search-plus" aria-hidden="true"></i> Preview email
                </a>
              </div>
              <div className="col-md-4 aright">
                {shell.data.ready && (
                  <button type="button" className="btn btn-xs btn-primary" onClick={openNew}>
                    Customize
                  </button>
                )}
              </div>
            </div>
          </section>
        ) : (
          templates.map((template) => (
            <section className="settings" key={template.template_id}>
              <div className="row">
                <div className="col-md-3">
                  <h5>
                    <i className="far fa-envelope-open" aria-hidden="true"></i> {template.template_name}
                  </h5>
                  {template.is_default ? (
                    <p className="color-green3 tp-default" style={{ marginTop: 5 }}>
                      <i className="fas fa-star" aria-hidden="true"></i> Default
                    </p>
                  ) : null}
                </div>
                <div className="col-md-3" style={{ wordWrap: 'break-word' }}>
                  <p>{template.template_subject}</p>
                  <a
                    href="#"
                    className="color-blue"
                    onClick={(e) => {
                      e.preventDefault()
                      setPreview(previewOf(template.template_subject, template.template_body))
                    }}
                  >
                    <i className="fas fa-search-plus" aria-hidden="true"></i> Preview email
                  </a>
                </div>
                <div className="col-md-2 tp-providers-cell" style={{ wordWrap: 'break-word' }}>
                  <span className="updated">
                    {(template.providers ?? []).length > 0 ? (template.providers ?? []).map((p) => p.name).join(', ') : 'General'}
                  </span>
                  <p className="color-green3">
                    <i className="fas fa-building" aria-hidden="true"></i> Providers
                  </p>
                </div>
                <div className="col-md-1">
                  <span className="updated">{updatedLabel(template.updated)}</span>
                  <p className="color-green3">
                    <i className="far fa-clock" aria-hidden="true"></i> Updated
                  </p>
                </div>
                <div className="col-md-3 aright">
                  {!template.is_default && (
                    <>
                      <button type="button" className="btn btn-xs btn-default tp-make-default" onClick={() => makeDefault.mutate(template.template_id)}>
                        Set as default
                      </button>{' '}
                    </>
                  )}
                  <button
                    type="button"
                    className="btn btn-xs btn-primary tp-edit"
                    onClick={() => {
                      setChosen((template.providers ?? []).map((p) => p.id))
                      setEdited(template)
                    }}
                  >
                    Edit
                  </button>{' '}
                  <button
                    type="button"
                    className="btn btn-xs btn-danger"
                    onClick={() => {
                      if (!window.confirm('Are you sure?')) return
                      remove.mutate(template.template_id)
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <hr className="dotted" />
            </section>
          ))
        )}
      </div>

      <Modal id="thirdPartyPreviewModal" labelledBy="thirdPartyPreviewModalLabel" open={preview !== null} onClose={() => setPreview(null)} title="Email preview">
        {preview && <TextPreview shown={preview} />}
      </Modal>

      <TemplateModal
        open={creating}
        template={null}
        options={shell.data.options}
        save={save}
        onClose={() => setCreating(false)}
        onSaved={reload}
        initial={builtin}
        preview={previewOf}
        extra={picker(null)}
      />
      <TemplateModal
        open={edited !== null}
        template={edited}
        options={shell.data.options}
        save={save}
        onClose={() => setEdited(null)}
        onSaved={reload}
        preview={previewOf}
        extra={picker(edited)}
      />
    </div>
  )
}
